import React, { useEffect, useState, useRef } from "react";
import { useFirebase } from 'providers/firebase/FirebaseContext';
import { UserContext } from 'providers/user/UserContext';
import PendingApprovalPage from 'components/pending-approval/PendingApprovalPage';
import { identifyUser } from 'services/AnalyticsService';

const initialState = {
  loading: true,
  data: null
};

/**
 * Checks whether a Firestore user-profile document looks "complete" enough
 * for the rest of the app to work.  A valid profile must have a non-empty
 * `type` field.
 *
 * During sign-up there is a brief window where the Auth user exists but the
 * Firestore profile has not yet been written (or has only been partially
 * written).  Treating those intermediate snapshots as "loaded" causes the
 * rest of the app to crash or show a white screen.
 *
 * NOTE: We accept ANY non-empty string type rather than a hardcoded allowlist
 * so that adding new user types (coach, trainer, etc.) never causes sign-in
 * to hang because UserProvider doesn't recognise the type.
 */
function isProfileComplete(userData) {
  return (
    userData != null &&
    typeof userData.type === 'string' &&
    userData.type.length > 0
  );
}

export default function UserProvider({ children }) {
  const firebase = useFirebase();
  const [user, setUser] = useState(initialState);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    let removeProfileListener = () => { };
    let incompleteProfileTimer = null;

    const removeAuthChangeListener = firebase.auth.onAuthStateChanged(function (currentUser) {
      if (currentUser) {
        // Wrap analytics in try/catch so it never blocks profile loading
        try {
          firebase.analytics.setUserId(currentUser.uid);
          firebase.trackAnalyticsEvent('login');
        } catch (e) {
          // Analytics failure should never block profile loading
        }

        /**
         * If a sign-up is currently in progress, the Firestore profile
         * document hasn't been written yet.  Attaching a listener now
         * would see an empty / partial document and trigger the
         * race-condition code paths.
         *
         * Instead, we poll until `signUpInProgress` is cleared (the
         * profile write is done), THEN attach the listener so it sees
         * the complete document on its very first snapshot.
         */
        function attachProfileListener() {
          removeProfileListener = firebase.useProfile({
            next: function (snapshot) {
              if (!isMountedRef.current) return;
              var userData = snapshot.data();

              // testing athlete override
              if (userData != null && userData.email === 'test@newfiltering.com') {
                userData.type = 'athlete';
              }

              if (isProfileComplete(userData)) {
                // ── Happy path: full profile arrived ──
                if (incompleteProfileTimer) {
                  clearTimeout(incompleteProfileTimer);
                  incompleteProfileTimer = null;
                }

                setUser(function () {
                  return { loading: false, data: userData };
                });

                if (snapshot && snapshot.exists && snapshot.id) {
                  const uid = snapshot.id;
                  try { firebase.setUserId(uid); } catch (e) { }
                  // Set user properties for segmentation in both Firebase Analytics + Amplitude
                  try {
                    identifyUser(firebase, uid, {
                      user_type:    userData.type || '',
                      plan:         userData.subscriptionTier || userData.tier || 'free',
                      sport:        userData.sport || '',
                      organization: userData.organization || userData.orgName || '',
                    });
                  } catch (e) { }
                }
              } else if (snapshot.exists) {
                // Document exists but is missing `type` — unusual after
                // our sign-up fix, but handle gracefully with a timeout.

                if (!incompleteProfileTimer) {
                  incompleteProfileTimer = setTimeout(function () {
                    if (!isMountedRef.current) return;
                    setUser(function (prev) {
                      if (!prev.loading) return prev;
                      return { loading: false, data: userData || null };
                    });
                  }, 3000);
                }
              } else {
                // Document does not exist at all

                if (!incompleteProfileTimer) {
                  incompleteProfileTimer = setTimeout(function () {
                    if (!isMountedRef.current) return;
                    setUser(function (prev) {
                      if (!prev.loading) return prev;
                      return { loading: false, data: null };
                    });
                  }, 3000);
                }
              }
            },
            error: function (e) {
              if (!isMountedRef.current) return;
              console.error('UserProvider: onSnapshot error', e);
              setUser({ loading: false, data: null });
            }
          });
        }

        if (firebase.signUpInProgress) {
          // Sign-up is writing the profile — poll until it finishes
          var signUpPollInterval = setInterval(function () {
            if (!isMountedRef.current) {
              clearInterval(signUpPollInterval);
              return;
            }
            if (!firebase.signUpInProgress) {
              clearInterval(signUpPollInterval);
              console.log('UserProvider: sign-up complete, attaching profile listener');
              attachProfileListener();
            }
          }, 200);
        } else {
          // Normal login — attach listener immediately
          attachProfileListener();
        }
      } else {
        // User signed out
        removeProfileListener();
        if (incompleteProfileTimer) {
          clearTimeout(incompleteProfileTimer);
          incompleteProfileTimer = null;
        }
        if (isMountedRef.current) {
          setUser({ loading: false, data: null });
        }
      }
    });

    return function () {
      isMountedRef.current = false;
      if (incompleteProfileTimer) {
        clearTimeout(incompleteProfileTimer);
      }
      removeProfileListener();
      removeAuthChangeListener();
    };
  }, [firebase]);

  // If a logged-in user is pending approval, show the holding page.
  // The onSnapshot listener above updates `user.data` in real-time, so
  // the moment an admin sets approvalStatus → 'approved', this clears automatically.
  const userData = user.data;
  const isPendingUser =
    !user.loading &&
    userData != null &&
    userData.approvalStatus &&
    userData.approvalStatus !== 'approved' &&
    userData.approvalStatus !== 'onboarding';

  if (isPendingUser) {
    const firstName = userData.displayName
      ? userData.displayName.split(' ')[0]
      : userData.firstName || '';
    return <PendingApprovalPage firstName={firstName} />;
  }

  return (
    <UserContext.Provider value={user}>
      {children}
    </UserContext.Provider>
  );
}
